// Utility functions for habit scheduling and management

// Get the start of day for consistent date comparisons
export const getStartOfDay = (date = new Date()) => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay;
};

// Format date as YYYY-MM-DD for consistent storage
export const formatDateKey = (date) => {
  return date.toISOString().split('T')[0];
};

// Calculate next occurrence for weekly habits
export const calculateNextWeeklyOccurrence = (lastCompleted, timesPerWeek = 1) => {
  const daysInterval = Math.floor(7 / timesPerWeek);
  const nextDate = new Date(lastCompleted);
  nextDate.setDate(nextDate.getDate() + daysInterval);
  return getStartOfDay(nextDate);
};

// Calculate next occurrence for monthly habits
export const calculateNextMonthlyOccurrence = (lastCompleted) => {
  const nextDate = new Date(lastCompleted);
  nextDate.setMonth(nextDate.getMonth() + 1);
  return getStartOfDay(nextDate);
};

// Helper function to distribute habits across different days
const getOptimalSchedulingDay = (habitType, index, baseDate) => {
  const scheduledDate = new Date(baseDate);

  if (habitType === 'weekly') {
    // Distribute weekly habits across the week
    // Pattern: Sunday(0), Tuesday(2), Thursday(4), Saturday(6), Monday(1), Wednesday(3), Friday(5)
    const dayPattern = [0, 2, 4, 6, 1, 3, 5];
    const targetDay = dayPattern[index % dayPattern.length];
    const currentDay = scheduledDate.getDay();
    const daysToAdd = (targetDay - currentDay + 7) % 7;
    scheduledDate.setDate(scheduledDate.getDate() + daysToAdd);
  } else if (habitType === 'monthly') {
    // Distribute monthly habits across different weeks
    // Week 1: day 3-5, Week 2: day 10-12, Week 3: day 17-19, Week 4: day 24-26
    const weekOffset = Math.floor(index / 2); // 2 habits per week max
    const dayInWeek = 3 + (index % 2) * 2; // Day 3 or 5 of the week
    const targetDate = weekOffset * 7 + dayInWeek;
    scheduledDate.setDate(scheduledDate.getDate() + targetDate);
  }

  return getStartOfDay(scheduledDate);
};

// Initialize habit schedules for a new user
export const initializeHabitSchedules = (personalizedHabits) => {
  const today = getStartOfDay();
  const schedules = {};

  // Separate habits by frequency for better scheduling
  const weeklyHabits = personalizedHabits.filter(h => h.frequency === 'weekly');
  const monthlyHabits = personalizedHabits.filter(h => h.frequency === 'monthly');

  personalizedHabits.forEach((habit, index) => {
    const habitId = habit.id;

    if (habit.frequency === 'daily') {
      // Daily habits don't need scheduling - they appear every day
      schedules[habitId] = {
        frequency: 'daily',
        nextOccurrence: today,
        lastCompleted: null,
        completionHistory: {}
      };
    } else if (habit.frequency === 'weekly') {
      // Spread weekly habits across different days of the week
      const weeklyIndex = weeklyHabits.findIndex(h => h.id === habitId);
      const scheduledDate = getOptimalSchedulingDay('weekly', weeklyIndex, today);

      const timesPerWeek = habit.timesPerWeek || 1;
      schedules[habitId] = {
        frequency: 'weekly',
        timesPerWeek,
        nextOccurrence: scheduledDate,
        lastCompleted: null,
        completionHistory: {}
      };
    } else if (habit.frequency === 'monthly') {
      // Spread monthly habits across different weeks of the month
      const monthlyIndex = monthlyHabits.findIndex(h => h.id === habitId);
      const scheduledDate = getOptimalSchedulingDay('monthly', monthlyIndex, today);

      schedules[habitId] = {
        frequency: 'monthly',
        nextOccurrence: scheduledDate,
        lastCompleted: null,
        completionHistory: {}
      };
    }
  });

  return schedules;
};

// Get habits that should appear on a specific date
export const getHabitsForDate = (personalizedHabits, habitSchedules, targetDate) => {
  const targetDateKey = formatDateKey(targetDate);
  const habitsForDate = [];

  personalizedHabits.forEach(habit => {
    const schedule = habitSchedules[habit.id];
    if (!schedule) return;

    const isScheduledForDate = isHabitScheduledForDate(habit, schedule, targetDate);
    const isCompleted = schedule.completionHistory[targetDateKey] || false;

    if (isScheduledForDate) {
      habitsForDate.push({
        ...habit,
        isCompleted,
        scheduledDate: targetDate,
        schedule
      });
    }
  });

  // Sort habits by priority and time of day
  return habitsForDate.sort((a, b) => {
    // High priority habits first
    if (a.priority === 'high' && b.priority !== 'high') return -1;
    if (b.priority === 'high' && a.priority !== 'high') return 1;
    
    // Then by time of day
    const timeOrder = { morning: 1, all: 2, evening: 3 };
    return (timeOrder[a.timeOfDay] || 2) - (timeOrder[b.timeOfDay] || 2);
  });
};

// Check if a habit should appear on a specific date
const isHabitScheduledForDate = (habit, schedule, targetDate) => {
  const targetTime = targetDate.getTime();
  const nextOccurrenceTime = schedule.nextOccurrence.getTime();

  if (habit.frequency === 'daily') {
    return true; // Daily habits appear every day
  }

  // For weekly and monthly habits, check if target date matches next occurrence
  return targetTime >= nextOccurrenceTime;
};

// Mark a habit as completed and update schedule
export const completeHabit = (habitId, completionDate, habitSchedules, personalizedHabits) => {
  const schedule = habitSchedules[habitId];
  const habit = personalizedHabits.find(h => h.id === habitId);
  
  if (!schedule || !habit) return habitSchedules;

  const dateKey = formatDateKey(completionDate);
  const updatedSchedules = { ...habitSchedules };
  
  // Update completion history
  updatedSchedules[habitId] = {
    ...schedule,
    completionHistory: {
      ...schedule.completionHistory,
      [dateKey]: true
    },
    lastCompleted: getStartOfDay(completionDate)
  };

  // Calculate next occurrence for weekly and monthly habits
  if (habit.frequency === 'weekly') {
    updatedSchedules[habitId].nextOccurrence = calculateNextWeeklyOccurrence(
      completionDate, 
      habit.timesPerWeek || 1
    );
  } else if (habit.frequency === 'monthly') {
    updatedSchedules[habitId].nextOccurrence = calculateNextMonthlyOccurrence(completionDate);
  }

  return updatedSchedules;
};

// Calculate completion percentage for a date
export const calculateDayCompletion = (habitsForDate) => {
  if (habitsForDate.length === 0) return 0;
  
  const completedCount = habitsForDate.filter(habit => habit.isCompleted).length;
  return Math.round((completedCount / habitsForDate.length) * 100);
};

// Get completion streak (consecutive days with 100% completion)
export const getCompletionStreak = (habitSchedules, personalizedHabits, endDate = new Date()) => {
  let streak = 0;
  let currentDate = getStartOfDay(endDate);
  
  // Go backwards day by day until we find an incomplete day
  for (let i = 0; i < 30; i++) { // Check up to 30 days back
    const habitsForDate = getHabitsForDate(personalizedHabits, habitSchedules, currentDate);
    const completion = calculateDayCompletion(habitsForDate);
    
    if (completion === 100 && habitsForDate.length > 0) {
      streak++;
    } else {
      break;
    }
    
    // Move to previous day
    currentDate.setDate(currentDate.getDate() - 1);
  }
  
  return streak;
};

// Get upcoming weekly and monthly habits for preview
export const getUpcomingHabits = (personalizedHabits, habitSchedules, daysAhead = 7) => {
  const today = getStartOfDay();
  const upcomingHabits = [];

  for (let i = 1; i <= daysAhead; i++) {
    const checkDate = new Date(today);
    checkDate.setDate(today.getDate() + i);

    const habitsForDate = getHabitsForDate(personalizedHabits, habitSchedules, checkDate);
    const weeklyAndMonthly = habitsForDate.filter(h => h.frequency !== 'daily');

    if (weeklyAndMonthly.length > 0) {
      upcomingHabits.push({
        date: checkDate,
        habits: weeklyAndMonthly
      });
    }
  }

  return upcomingHabits;
};

// Get a summary of the habit schedule distribution
export const getScheduleSummary = (personalizedHabits, habitSchedules) => {
  const summary = {
    daily: 0,
    weekly: [],
    monthly: []
  };

  personalizedHabits.forEach(habit => {
    const schedule = habitSchedules[habit.id];
    if (!schedule) return;

    if (habit.frequency === 'daily') {
      summary.daily++;
    } else if (habit.frequency === 'weekly') {
      const dayName = schedule.nextOccurrence.toLocaleDateString('en-US', { weekday: 'long' });
      summary.weekly.push({
        name: habit.name,
        day: dayName,
        date: schedule.nextOccurrence
      });
    } else if (habit.frequency === 'monthly') {
      summary.monthly.push({
        name: habit.name,
        date: schedule.nextOccurrence
      });
    }
  });

  return summary;
};
